// Mirrors internal/auth/scope.go - the main app and each single-module app are locked,
// allow/block-listed and geo-blocked independently.
export const ACCESS_SCOPE_MAIN_APP = "myproject";
export const ACCESS_SCOPE_DECK_DESIGNER = "deck-designer-terralock";
export const ACCESS_SCOPE_LOCK_CALCULATOR = "lock-calculator";
export const ACCESS_SCOPE_CONCRETE_RESIN = "concrete-resin-quantity";
export const ACCESS_SCOPE_US_LIFTING_CALCULATOR = "us-lifting-calculator";
// A second app of the US lifting calculator module, with its own scope.
export const ACCESS_SCOPE_RAPTOR_CALCULATOR = "raptor-calculator";

export type AccessScope =
  | typeof ACCESS_SCOPE_MAIN_APP
  | typeof ACCESS_SCOPE_DECK_DESIGNER
  | typeof ACCESS_SCOPE_LOCK_CALCULATOR
  | typeof ACCESS_SCOPE_CONCRETE_RESIN
  | typeof ACCESS_SCOPE_US_LIFTING_CALCULATOR
  | typeof ACCESS_SCOPE_RAPTOR_CALCULATOR;

export const ACCESS_SCOPES: AccessScope[] = [
  ACCESS_SCOPE_MAIN_APP,
  ACCESS_SCOPE_DECK_DESIGNER,
  ACCESS_SCOPE_LOCK_CALCULATOR,
  ACCESS_SCOPE_CONCRETE_RESIN,
  ACCESS_SCOPE_US_LIFTING_CALCULATOR,
  ACCESS_SCOPE_RAPTOR_CALCULATOR,
];

// The apps' titles, e.g. the access control tabs and the usage statistics columns.
export const ACCESS_SCOPE_TITLES: Record<AccessScope, string> = {
  [ACCESS_SCOPE_MAIN_APP]: "Main App",
  [ACCESS_SCOPE_DECK_DESIGNER]: "Deck Designer",
  [ACCESS_SCOPE_LOCK_CALCULATOR]: "Lock Calculator",
  [ACCESS_SCOPE_CONCRETE_RESIN]: "Resin Quantity",
  [ACCESS_SCOPE_US_LIFTING_CALCULATOR]: "US Lifting Calculator",
  [ACCESS_SCOPE_RAPTOR_CALCULATOR]: "Raptor Calculator",
};

/**
 * Request header telling the backend which app a request comes from, so it applies that app's
 * lock switch and allow/block list. Its value is the app's scope above, fixed per build by the
 * `scope` each entry file (apps/<app>/index.tsx) passes to <App>.
 *
 * Why it's needed: all apps call the same /api with the same login token, and share endpoints
 * such as /api/calculations, so neither the token nor the URL tells the apps apart.
 *
 * Rule: every request to /api must send it. Use useApi, or useApiHeaders where useApi can't be used
 * (cached module-level fetches, XHR uploads with progress); never a plain fetch with only the
 * Authorization header. Without it the backend falls back to the main app's rules, and a user
 * allowed in a single-module app but refused by the main app gets 403 (e.g. the Lock-T 3D models).
 *
 * The name must match AccessScopeHeader in internal/auth/scope.go, read by the JWT middleware
 * (internal/middlewares/jwt.go). It's sent by the client, so it keeps normal users out but is not
 * a hard security boundary.
 */
export const ACCESS_SCOPE_HEADER = "X-App-Scope";

export type AccessRuleStatus = "allowed" | "blocked";

// What access settings and rules apply to: an app, or a module in the main app only (its own app, if
// any, has its scope's) - mirrors AccessTarget in internal/models/dto/access.go.
export type AccessTarget = { scope: AccessScope } | { moduleId: string };

export type AppSettings = {
  locked: boolean;
};

// A rule of an app (scope) or of a module (moduleId), one of the two.
export type AccessRule = {
  id: string;
  scope?: AccessScope;
  moduleId?: string;
  email: string;
  status: AccessRuleStatus;
  reason?: string;
  createdAt: string;
  // the rule's user, once known (logged in, or stored with the rule's email)
  user?: { id: string; email: string };
};

// Why a user is allowed in an app or not - mirrors UserAppAccessCause* in internal/models/dto/access.go.
export type UserAppAccessCause = "rule" | "default" | "admin" | "locked";

// A user's access to an app, decided like the backend's access check.
export type UserAppAccess = {
  scope: AccessScope;
  status: AccessRuleStatus;
  cause: UserAppAccessCause;
  rule?: AccessRule;
};

export type UpsertAccessRuleBody = {
  email: string;
  status: AccessRuleStatus;
  reason?: string;
};

// A rule of the app or module to import from, with the status of the same email's rule in the one to
// import into, if any. The current user's own rule can't be imported.
export type AccessRuleImport = {
  email: string;
  status: AccessRuleStatus;
  reason?: string;
  currentStatus?: AccessRuleStatus;
  isCurrentUser: boolean;
};

// Imports from an app (fromScope) or a module (fromModuleId).
export type ImportAccessRulesBody = {
  fromScope?: AccessScope;
  fromModuleId?: string;
  emails: string[];
};

/**
 * Set when the current user can't use the app right now, derived from a 403 error code. Blocked,
 * locked out and geo-blocked users all get the same plain "forbidden": nothing tells them why.
 */
export type AccessDeniedReason = "forbidden" | null;

// Error codes the backend's access check answers with (HTTP 403) - mirrors internal/errors/errors.go.
export const ACCESS_BLOCKED_CODE = 4100001;
export const ACCESS_APP_LOCKED_CODE = 4100002;
export const ACCESS_GEO_BLOCKED_CODE = 4100016;

/** Maps a failed API response to the app-level access denial it carries, or null for any other error. */
export const getAccessDeniedReason = (error: any): AccessDeniedReason => {
  const code = error?.json?.error?.code;
  // no hint of why, and they get in as soon as the rules let them
  if (code === ACCESS_BLOCKED_CODE || code === ACCESS_APP_LOCKED_CODE || code === ACCESS_GEO_BLOCKED_CODE) {
    return "forbidden";
  }
  return null;
};
